import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Clock, CheckCircle2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const Messages = () => {
  const { user, API } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/broadcast`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.post(`${API}/messages/broadcast/${messageId}/read`);
      // Update local state
      setMessages(messages.map(msg => 
        msg.id === messageId 
          ? { ...msg, read_by: [...(msg.read_by || []), user.id] }
          : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDelete = async (e, messageId) => {
    // Stop the card's click-to-mark-read from also firing.
    e.stopPropagation();
    if (!window.confirm('Remove this message from your inbox? Other members can still see it.')) return;
    try {
      await axios.delete(`${API}/messages/broadcast/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success('Message removed from your inbox');
    } catch (err) {
      toast.error('Failed to remove message');
    }
  };

  const isRead = (message) => {
    return message.read_by?.includes(user?.id);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Messages
          </h1>
          <p className="text-slate-600">Important updates from church leadership</p>
        </div>

        {loading ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <p className="text-slate-600">Loading messages...</p>
            </CardContent>
          </Card>
        ) : messages.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                No Messages Yet
              </h3>
              <p className="text-slate-600">
                You'll receive important updates and announcements here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <Card
                key={message.id}
                className={`border-l-4 ${
                  isRead(message) ? 'border-l-slate-300' : 'border-l-[#2B6B7F]'
                } cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => !isRead(message) && markAsRead(message.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <CardTitle className="text-xl text-slate-800">
                          {message.title}
                        </CardTitle>
                        {!isRead(message) && (
                          <Badge className="bg-[#2B6B7F]">New</Badge>
                        )}
                        {isRead(message) && (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="font-medium">{message.sender_name}</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(message.sent_at)}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDelete(e, message.id)}
                      className="flex-shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      title="Remove from my inbox"
                      data-testid={`delete-message-${message.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {message.message}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
